import * as breakpoint from '@pixelunion/breakpoint';
import EventHandler from '@pixelunion/events';
import { transition } from '@pixelunion/animations';
import rimg from '@pixelunion/rimg-shopify';
import ThemeUtils from '../utils/ThemeUtils';

export default class PredictiveSearch {
  constructor(el) {
    this.el = el;
    this.input = this.el.querySelector('[data-search-input]');
    this.searchFlyoutForm = this.el.querySelector('[data-search-flyout-form]');
    this.predictiveSearchContainer = this.el.querySelector('[data-predictive-search-container]');
    this.predictiveSearchResultsContainer = this.el.querySelector('[data-predictive-search-results-container]');
    this.predictiveSearchContainerAnimation = transition({ el: this.predictiveSearchContainer, state: 'closed' });

    this.events = new EventHandler();
    this.cachedResults = new Map();
    this.isOpen = false;

    this._positionSearchContainer();

    this._setupEventListeners();
  }

  _setupEventListeners() {
    this.events.register(window, 'keyup', e => this._onKeyUp(e));
    this.events.register(window, 'resize', ThemeUtils.debounce(() => this._onResize(), 150));
    this.events.register(this.el, 'submit', e => this._onSubmit(e));
    this.events.register(this.input, 'input', ThemeUtils.debounce(() => this._onChange(), 150));
    this.events.register(this.input, 'focus', e => this._onFocus(e));
    this.events.register(this.input, 'keydown', e => this._onKeydown(e));
  }

  get _searchQuery() {
    return this.input.value.trim();
  }

  get _isMobile() {
    return breakpoint.max('S');
  }

  _positionSearchContainer() {
    if (this._isMobile) {
      this.el.append(this.predictiveSearchContainer);
    } else {
      this.searchFlyoutForm.append(this.predictiveSearchContainer);
    }
  }

  _onChange() {
    if (this._searchQuery.length === 0) {
      this.close();
      return;
    }

    this._getSearchResults(this._searchQuery);
  }

  _onFocus() {
    if (this._searchQuery.length === 0) return;

    this._getSearchResults(this._searchQuery);
  }

  _onSubmit(e) {
    if (this._searchQuery.length === 0) e.preventDefault();
  }

  _onKeyUp(e) {
    if (e.code === 'Escape') this.close();
  }

  _onKeydown(e) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (e.code === 'ArrowUp' || e.code === 'ArrowDown') {
      e.preventDefault();
    }
  }

  _onResize() {
    this._positionSearchContainer();
    this._resizeSearchResults();
  }

  _getSearchResults(searchTerm) {
    const cachedResult = this.cachedResults.get(searchTerm);

    this._setLoadingState();

    if (cachedResult) {
      this._renderSearchResults(cachedResult);
      return;
    }

    fetch(`${window.Theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`)
      .then(response => {
        if (!response.ok) {
          this.close();
          throw new Error(response.status);
        }
        return response.text();
      })
      .then(text => {
        this.cachedResults.set(searchTerm, text);
        this._renderSearchResults(text);
      })
      .catch(error => {
        this.close();
        throw error;
      });
  }

  _setLoadingState() {
    this.predictiveSearchContainer.setAttribute('data-loading', 'true');
    this.clearResults();
    if (!this.isOpen) this._open();
  }

  _removeLoadingState() {
    this.predictiveSearchContainer.setAttribute('data-loading', 'false');
  }

  _resizeSearchResults() {
    const offset = 30;
    const position = this.input.getBoundingClientRect();
    const maxHeight = window.innerHeight - position.bottom - offset;

    this.predictiveSearchContainer.style.setProperty('--container-max-height', `${maxHeight}px`);
  }

  _renderSearchResults(resultsMarkup) {
    this._removeLoadingState();
    this.predictiveSearchResultsContainer.innerHTML = resultsMarkup;
    this._resizeSearchResults();
    rimg.watch(this.predictiveSearchResultsContainer);
  }

  _open() {
    if (this.isOpen) return;

    this.searchFlyoutForm.setAttribute('data-flydown-open', 'true');

    this.predictiveSearchContainerAnimation.animateTo('open')
      .then(() => {
        this.isOpen = true;
      });
  }

  close() {
    if (!this.isOpen) return false;

    this.searchFlyoutForm.setAttribute('data-flydown-open', 'false');

    return this.predictiveSearchContainerAnimation.animateTo('closed')
      .then(() => {
        this.clearResults();
        this.isOpen = false;
      });
  }

  clearResults() {
    const el = this.predictiveSearchResultsContainer;

    // This should be more performant than `innerHTMl = ""`
    while (el.firstChild) el.removeChild(el.firstChild);
  }
}
